import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Download } from 'lucide-react';
import { isIOS } from '@/lib/installManager';
import { isStandalone } from '@/lib/deviceDetect';

const DISMISS_KEY = 'papacito_ios_install_banner_dismissed_at';
const DISMISS_MS = 3 * 24 * 60 * 60 * 1000;
const SHOW_AFTER_MS = 30 * 1000;
const MIN_SWIPES_BEFORE_SHOW = 20;

// Routes where the nag banner must never appear. The post-onboarding
// install screen is the designated "first ask" — showing a second ask
// during signup, onboarding, or auth flows is too noisy.
const SUPPRESSED_PATHS = [
  '/login',
  '/register',
  '/verify-email',
  '/onboarding',
  '/auth',
  '/forgot-password',
  '/reset-password',
  '/install-prompt',
  '/push-prompt',
  '/vip-offer',
  '/install-app',
  '/subscription',
  '/delete-account',
  '/legal',
  '/privacy',
  '/terms',
];

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_MS;
  } catch {
    return false;
  }
}

function getSwipeCount(): number {
  try {
    const raw = localStorage.getItem('papacito_swipe_count') ?? '0';
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function IOSInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [location] = useLocation();

  const isSuppressedPath = SUPPRESSED_PATHS.some(p => location.startsWith(p));

  useEffect(() => {
    if (!isIOS() || isStandalone()) return;
    if (isDismissed()) return;
    if (isSuppressedPath) return;
    if (getSwipeCount() < MIN_SWIPES_BEFORE_SHOW) return;
    const t = window.setTimeout(() => {
      if (!isDismissed()) setVisible(true);
    }, SHOW_AFTER_MS);
    return () => window.clearTimeout(t);
  }, [isSuppressedPath]);

  if (isSuppressedPath) return null;

  if (!visible) return null;

  const dismiss = (): void => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const handleInstall = (): void => {
    alert('To install Papacito:\n\n1. Tap the Share button at the bottom of Safari\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" in the top right');
    dismiss();
  };

  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
        zIndex: 80,
        padding: '0 12px',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          background: 'linear-gradient(135deg, rgba(225,29,72,0.97) 0%, rgba(190,18,60,0.97) 100%)',
          borderRadius: 14,
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Download size={18} color="white" />
        </div>
        <p
          style={{
            flex: 1,
            color: 'white',
            fontSize: 13,
            lineHeight: 1.35,
            margin: 0,
            fontWeight: 600,
          }}
        >
          Never miss a match — Install Papacito to get instant alerts
        </p>
        <button
          onClick={handleInstall}
          style={{
            background: 'white',
            color: '#e11d48',
            border: 'none',
            borderRadius: 8,
            padding: '7px 12px',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Install
        </button>
        <button
          onClick={dismiss}
          style={{
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.5)',
            borderRadius: 8,
            color: 'white',
            padding: '7px 10px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
